import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Clock, Search, RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';
import * as revenueService from '../services/revenueService';
import { useAuth } from '../context/AuthContext';
import { buildApiUrl } from '../services/apiHelpers';

/**
 * PendingPaymentsWidget - Widget to display and manage pending payments
 */
const PendingPaymentsWidget = ({
  onOpenPayment
}) => {
  const { auth } = useAuth();
  const [pendingPayments, setPendingPayments] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [localSearchQuery, setLocalSearchQuery] = useState('');
  const searchTimeoutRef = useRef(null);
  const isInitialMount = useRef(true);

  // ═══════════════════════════════════════════════════════
  // DATA LOADING
  // ═══════════════════════════════════════════════════════

  const loadPendingPayments = useCallback(async (opts = {}) => {
    const { query = '' } = opts;
    setLoading(true);
    setError(null);

    try {
      const clinicId = auth?.clinic?.id || (typeof window !== 'undefined' && window.__CLINIC_ID__);
      const [response, invoicesResponse] = await Promise.all([
        revenueService.getPendingPayments(50, 0, null, null, query),
        clinicId
          ? fetch(buildApiUrl('clinic/patient-invoices'), { credentials: 'include', headers: { 'X-Clinic-Id': String(clinicId) } })
          : Promise.resolve(null)
      ]);
      let invoices = [];
      if (invoicesResponse?.ok) {
        const invoicePayload = await invoicesResponse.json();
        invoices = Array.isArray(invoicePayload?.data) ? invoicePayload.data : [];
      }
      const invoiceByAppointment = new Map(invoices.map(invoice => [String(invoice.appointmentId), invoice]));
      const pending = (response?.pending || [])
        .map(payment => ({ ...payment, patientInvoice: invoiceByAppointment.get(String(payment.appointmentId)) || null }))
        .filter(payment => payment.patientInvoice?.paymentStatus !== 'PAID');
      setPendingPayments({
        ...response,
        pending,
        total: pending.length,
        totalPendingAmount: pending.reduce((sum, payment) => sum + Number(payment.pending || 0), 0)
      });
    } catch (err) {
      setError(err.message || 'Failed to load pending payments');
      console.error('Pending payments error:', err);
    } finally {
      setLoading(false);
    }
  }, [auth?.clinic?.id]);

  // ═══════════════════════════════════════════════════════
  // EFFECTS
  // ═══════════════════════════════════════════════════════

  useEffect(() => {
    loadPendingPayments({ query: '' });
  }, [loadPendingPayments]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      loadPendingPayments({ query: localSearchQuery });
    }, 500);

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [localSearchQuery, loadPendingPayments]);

  // Listen for payment collection events
  useEffect(() => {
    const handlePaymentCollected = () => {
      // Reload pending payments when payment is collected from OPD session
      setTimeout(() => loadPendingPayments({ query: localSearchQuery || '' }), 500);
    };

    window.addEventListener('paymentCollected', handlePaymentCollected);
    window.addEventListener('invoiceUpdated', handlePaymentCollected);
    return () => {
      window.removeEventListener('paymentCollected', handlePaymentCollected);
      window.removeEventListener('invoiceUpdated', handlePaymentCollected);
    };
  }, [loadPendingPayments, localSearchQuery]);

  // ═══════════════════════════════════════════════════════
  // HANDLERS
  // ═══════════════════════════════════════════════════════

  const handleSearch = (query) => {
    const normalizedQuery = query || '';
    setLocalSearchQuery(normalizedQuery);
  };

  const handleCollectPayment = (payment) => {
    if (onOpenPayment) {
      const invoiceStatus = payment.patientInvoice?.invoiceStatus;
      const appointmentData = {
        id: payment.appointmentId,
        appointmentId: payment.appointmentId,
        patientId: payment.patientId,
        patientName: payment.patientName,
        consultationFee: payment.consultationFeeAssigned || payment.consultationFee,
        appointmentDate: payment.appointmentDate,
        pendingAmount: payment.pending,
        patientInvoice: payment.patientInvoice || null,
        invoiceStatus
      };
      onOpenPayment(appointmentData);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // ═══════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════

  return (
    <div className="pending-payments-widget">
        <style>{`
          .pending-payments-widget { padding: 18px 22px 22px; }
          .widget-header { display: flex; align-items: center; justify-content: space-between; gap: 16px; }
          .header-left { display: flex; align-items: center; gap: 14px; min-width: 0; }
          .header-icon { width: 38px; height: 38px; min-width: 38px; border-radius: 12px; background: rgba(59,130,246,0.12); color: #1e40af; display: flex; align-items: center; justify-content: center; padding: 8px; }
          .widget-title { margin: 0; font-size: 18px; font-weight: 700; color: #0f172a; font-family: 'Poppins',sans-serif; }
          .widget-subtitle { margin: 4px 0 0; font-size: 13px; color: #475569; }
          .header-actions { display: flex; align-items: center; }
          .btn-refresh { width: 42px; height: 42px; border: 1px solid rgba(15,23,42,0.08); border-radius: 14px; background: #fff; display: flex; align-items: center; justify-content: center; cursor: pointer; color: #334155; transition: all 0.2s ease; }
          .btn-refresh:hover { border-color: rgba(59,130,246,0.18); color: #1e40af; background: #f8fbff; }
          .widget-search { margin-top: 18px; }
          .search-input-wrapper { display: flex; align-items: center; gap: 10px; background: #f8fafc; border: 1px solid rgba(15,23,42,0.08); border-radius: 14px; padding: 10px 14px; }
          .search-icon { color: #64748b; }
          .search-input { flex: 1; border: none; outline: none; background: transparent; font-size: 14px; color: #0f172a; min-width: 0; }
          .search-input::placeholder { color: rgba(71,85,105,0.58); }
          .widget-error, .widget-loading, .empty-state { margin-top: 18px; border: 1px solid rgba(15,23,42,0.08); border-radius: 18px; background: #ffffff; padding: 20px; display: flex; align-items: center; gap: 10px; color: #334155; }
          .widget-error svg, .widget-loading svg, .empty-state svg { min-width: 48px; min-height: 48px; color: #1e40af; }
          .widget-loading { justify-content: center; }
          .empty-state { flex-direction: column; text-align: center; justify-content: center; padding: 24px 20px; }
          .empty-state h4 { margin: 0; font-size: 18px; font-weight: 700; color: #0f172a; }
          .empty-state p { margin: 8px 0 0; font-size: 14px; color: #475569; }
          .pending-summary { display: flex; gap: 14px; flex-wrap: wrap; margin-top: 18px; }
          .summary-item { display: flex; flex-direction: column; gap: 6px; min-width: 150px; background: #f8fafc; border: 1px solid rgba(15,23,42,0.08); border-radius: 16px; padding: 14px 16px; }
          .summary-label { font-size: 12px; font-weight: 600; color: #475569; }
          .summary-value { font-size: 16px; font-weight: 700; color: #0f172a; }
          .pending-amount { color: #dc2626; }
          .pending-list { margin-top: 18px; display: grid; gap: 14px; }
          .pending-item { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 18px; border: 1px solid rgba(15,23,42,0.08); border-radius: 18px; background: #ffffff; }
          .pending-info { flex: 1; min-width: 0; }
          .patient-name { font-size: 15px; font-weight: 700; color: #0f172a; margin-bottom: 6px; }
          .appointment-details { display: flex; flex-direction: column; gap: 2px; align-items: flex-start; font-size: 13px; color: #475569; }
          .pending-amounts { display: flex; flex-direction: column; align-items: flex-end; gap: 6px; min-width: 160px; justify-content: flex-start; text-align:right; }
          .amount-info { text-align: right; display:flex; flex-direction:column; align-items:flex-end; gap:6px; }
          .total-amount { display: block; font-size: 15px; font-weight: 700; color: #0f172a; line-height:1; }
          .pending-label { display: block; font-size: 13px; color: #d97706; font-weight: 600; }
          .btn-collect { display: inline-flex; align-items: center; justify-content: center; gap: 8px; padding: 8px 14px; border: 1px solid rgba(59,130,246,0.18); border-radius: 12px; background: #eff6ff; color: #1d4ed8; font-weight: 700; cursor: pointer; transition: all 0.15s ease; min-width:84px; white-space:nowrap; margin-top:4px; }
          .btn-collect:hover { transform: translateY(-1px); background: #dde8ff; }
          /* Keep horizontal row layout on most phone sizes for visual parity with design */
          @media (max-width: 420px) {
            .pending-item { flex-direction: row; align-items: center; gap: 12px; }
            .pending-amounts { justify-content: flex-end; min-width: 120px; }
            .appointment-details { font-size: 13px; }
          }
        `}</style>
        <div className="widget-header">
          <div className="header-left">
            <Clock className="header-icon" />
            <div>
              <h3 className="widget-title">Pending Payments</h3>
              <p className="widget-subtitle">
                {pendingPayments ? `${pendingPayments.total || 0} pending collections` : 'Track outstanding payments'}
              </p>
            </div>
          </div>

          <div className="header-actions">
            <button
              type="button"
              className="btn-refresh"
              onClick={() => loadPendingPayments({ query: localSearchQuery })}
              disabled={loading}
              title="Refresh pending payments"
            >
              <RefreshCw size={16} className={loading ? 'spin' : ''} />
            </button>
          </div>
        </div>
      {/* Search Bar */}
      <div className="widget-search">
        <div className="search-input-wrapper">
          <Search size={16} className="search-icon" />
          <input
            type="text"
            placeholder="Search by patient name or phone..."
            value={localSearchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
            className="search-input"
          />
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="widget-error">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="widget-loading">
          <RefreshCw size={20} className="spin" />
          <span>Loading pending payments...</span>
        </div>
      )}

      {/* Content */}
      {!loading && !error && pendingPayments && (
        <>
          {/* Summary */}
          {pendingPayments.total > 0 && (
            <div className="pending-summary">
              <div className="summary-item">
                <span className="summary-label">Total Pending:</span>
                <span className="summary-value pending-amount">
                  {formatCurrency(pendingPayments.totalPendingAmount || 0)}
                </span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Appointments:</span>
                <span className="summary-value">{pendingPayments.total || 0}</span>
              </div>
            </div>
          )}

          {/* Payments List */}
          <div className="pending-list">
            {pendingPayments.pending && pendingPayments.pending.length > 0 ? (
              pendingPayments.pending.map((payment, idx) => (
                <div key={idx} className="pending-item">
                  <div className="pending-info">
                    <div className="patient-name">
                      {payment.patientName}
                    </div>
                    <div className="appointment-details">
                      <span className="appointment-date">
                        {formatDate(payment.appointmentDate)}
                      </span>
                      {payment.doctorName && (
                        <span className="doctor-name">• Dr. {payment.doctorName}</span>
                      )}
                    </div>
                  </div>

                  <div className="pending-amounts">
                    <div className="amount-info">
                      <span className="total-amount">
                        {formatCurrency(payment.consultationFeeAssigned || payment.consultationFee)}
                      </span>
                      <span className="pending-label">
                        Pending {formatCurrency(payment.pending)}
                      </span>
                    </div>

                    <button
                      type="button"
                      className="btn-collect"
                      onClick={() => handleCollectPayment(payment)}
                      title={payment.patientInvoice && payment.patientInvoice.invoiceStatus !== 'DRAFT' ? 'Collect payment' : 'Create invoice'}
                    >
                      {payment.patientInvoice && payment.patientInvoice.invoiceStatus !== 'DRAFT' ? <CheckCircle size={16} /> : <Clock size={16} />}
                      {payment.patientInvoice && payment.patientInvoice.invoiceStatus !== 'DRAFT' ? 'Collect' : 'Invoice'}
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">
                <CheckCircle size={48} className="empty-icon" />
                <h4>All payments collected!</h4>
                <p>No pending payments at this time.</p>
              </div>
            )}
          </div>
        </>
      )}

    </div>
  );
};

export default PendingPaymentsWidget;



